import { Avatar, List, Spin, Tag, Typography } from "antd";
import fallbackAvatar from "assets/images/fallbackAvatar.png";
import axios from "axios";
import { BLgroup, BLgroupMember } from "interfaces/internal";
import { Member } from "interfaces/users";
import React, { useEffect, useState, useRef } from "react";

interface Props {
  dataSource: Array<Member>;
  loading: boolean;
  onSelectMember: (member: Member) => void;
  groups?: BLgroup[];
}

export const SingleList: React.FC<Props> = ({ dataSource, loading, onSelectMember, groups }) => {
  const { Text } = Typography;
  const [memberGroups, setMemberGroups] = useState<Record<number, string[]>>({});
  const [pageSize, setPageSize] = useState<number>(34);
  const [loadingGroups, setLoadingGroups] = useState<Record<number, boolean>>({});
  const fetchedMembersRef = useRef<Set<number>>(new Set());

  useEffect(() => {
    if (!groups?.length) return;

    const activeMembers = dataSource.filter(member => member.is_active_member);
    const membersToFetch = activeMembers.filter(member => !fetchedMembersRef.current.has(member.id));

    if (membersToFetch.length === 0) return;

    const fetchGroupsForMember = async (member: Member) => {
      if (fetchedMembersRef.current.has(member.id)) return;

      setLoadingGroups(prev => ({ ...prev, [member.id]: true }));
      try {
        const response = await axios.get<BLgroupMember[]>(`/internal/${member.id}/groups/`);
        const groupNames = response.data
          .map(({ bl_group }) => groups.find(({ id }) => id === bl_group)?.name)
          .filter((name): name is string => !!name);

        setMemberGroups(prev => ({
          ...prev,
          [member.id]: groupNames,
        }));
        fetchedMembersRef.current.add(member.id);
        setLoadingGroups(prev => ({ ...prev, [member.id]: false }));
      } catch {
        setLoadingGroups(prev => ({ ...prev, [member.id]: false }));
      }
    };

    membersToFetch.forEach(member => fetchGroupsForMember(member));
  }, [dataSource, groups]);

  return (
    <List
      className="demo-loadmore-list"
      itemLayout="horizontal"
      loading={loading}
      pagination={{
        pageSize,
        hideOnSinglePage: true,
        onChange(page, size) {
          setPageSize(size);
        },
      }}
      dataSource={dataSource}
      locale={{
        emptyText: "Ingen Bindeleddere matcher søkekriteriene",
      }}
      renderItem={member => (
        <List.Item
          onClick={() => {
            onSelectMember(member);
            window.scrollTo({
              top: 0,
              behavior: "smooth",
            });
          }}
          style={{ cursor: "pointer" }}
        >
          <List.Item.Meta
            avatar={
              <Avatar
                size={48}
                src={member.profile_picture === "" || !member.profile_picture ? fallbackAvatar : member.profile_picture}
              />
            }
            title={
              <div>
                <Text strong>
                  {member.full_name}
                  {new Date(member.birthday).getDate() === new Date().getDate() && (
                    <span title={`${member.full_name.split(" ")[0]} har bursdag!`}> 🎂</span>
                  )}
                </Text>
                <div style={{ marginTop: 4 }}>
                  <Text type="secondary">
                    {member.mobile} | {member.seniority}
                  </Text>
                </div>
              </div>
            }
          />
          <div
            style={{
              marginLeft: "5px",
              display: "flex",
              alignItems: "center",
              maxWidth: "150px",
              flexWrap: "wrap",
              justifyContent: "flex-end",
            }}
          >
            {member.is_active_member && (
              <>
                {memberGroups[member.id]
                  ? memberGroups[member.id].map(g => (
                      <Tag.CheckableTag key={g} checked style={{ margin: 1 }}>
                        {g}
                      </Tag.CheckableTag>
                    ))
                  : loadingGroups[member.id] && <Spin size="small" />}
              </>
            )}
          </div>
        </List.Item>
      )}
    />
  );
};
